import React from 'react'
import { connect } from 'react-redux'
import { toastr } from 'react-redux-toastr'
import { compose } from 'recompose'

import CheckInForm from './CheckInForm'
import { fetchServiceTxns } from '../../api'
import {
  updateCheckInServiceTxnField,
  updateServiceInfo,
  runCodeScanInfo,
  clearCheckIn,
  upateProductPrice,
} from '../../actions/checkin'
import { info } from '../../storage'
import { withCheckInType } from './CheckInTypeContainer'
import CheckoutModal from '../../components/CheckoutModal'
import CheckInFormInfoContainer from './CheckInFormInfoContainer'
import { getIssuedOutTime, isCancelable } from '../../utils/serviceTxn'
import { startScan, stopScan } from '../../utils/codeScan'
import {
  withCameraScanModal,
  withKeyboardScanModal,
} from '../../components/ScanCodeModal'
import parseBarcode from '../../modules/barcodeParser'
import { createItem, fetchProduct } from '../../actions/remote_api'
import { ServiceTxnCancelConfirmModal } from '../../components/ServiceTxnCancelConfirmModal'
import { I18n } from 'react-redux-i18n'
import { isProductRequired } from '../../modules/productPrice'
import ProductSelectModal from './ProductSelectModal'

class CheckInFormContainer extends React.Component {
  state = {
    isCheckoutModalOpen: false,
    serviceTxnToCheckout: undefined,
    isProductModalOpen: false,
    productSearchText: '',
  }

  // Track which products we've already fetched to prevent infinite loops
  fetchedProductIds = new Set()

  loadServiceTxns = (searchWord) => {
    if (!searchWord || searchWord.length < 3) {
      return []
    }
    return fetchServiceTxns({
      q: searchWord,
      'service._id': info().serviceId,
      operationDaily: true,
      noCancel: true,
    })
  }

  travelClassOptions = [
    { label: 'Economy', value: 'Economy' },
    { label: 'Business', value: 'Business' },
    { label: 'First Class', value: 'First Class' },
  ]

  handleSubmit = (e) => {
    e.preventDefault()
    this.props.onSubmit()
  }

  handleServiceTxnSelect = (serviceTxn) => {
    this.props.loadServiceTxn(serviceTxn)
  }

  openCheckoutModal = () => {
    this.setState({
      ...this.state,
      isCheckoutModalOpen: true,
    })
  }

  handleCheckOut = (serviceTxn, outTime) => {
    this.props.updateCheckInServiceTxnField('serviceInfo.outTime', outTime)
    this.closeCheckoutModal()
    this.props.onSubmit()
  }

  closeCheckoutModal = () => {
    this.setState({
      ...this.state,
      isCheckoutModalOpen: false,
    })
  }

  openProductModal = () => {
    this.setState({
      ...this.state,
      isProductModalOpen: true,
    })
  }

  closeProductModal = () => {
    this.setState({
      ...this.state,
      isProductModalOpen: false,
      // Keep search text - don't clear it so modal remembers search
    })
  }

  handleProductSearchChange = (searchText) => {
    this.setState({
      ...this.state,
      productSearchText: searchText,
    })
  }

  handleProductSelect = (product) => {
    // Update productId in serviceTxn - selectedProduct will be found from the products list
    this.props.updateCheckInServiceTxnField('productId', product._id)

    // Update service info based on product selection
    const { serviceName, serviceClass } = info()
    this.props.updateServiceInfo(
      serviceName,
      serviceClass,
      this.props.defaultDurations,
    )
  }

  renderInfo = () => {
    if (!this.props.checkoutable) return null

    return (
      <CheckInFormInfoContainer
        inTime={this.props.serviceTxn.serviceInfo.inTime}
        limitTime={getIssuedOutTime(this.props.serviceTxn)}
        outTime={this.props.serviceTxn.serviceInfo.outTime}
      />
    )
  }

  handleFieldChange = (keyPath, value) => {
    this.props.updateCheckInServiceTxnField(keyPath, value)
    const { serviceName, serviceClass } = info()
    if (keyPath === 'travelClass')
      this.props.updateServiceInfo(
        serviceName,
        serviceClass,
        this.props.defaultDurations,
      )
  }

  handleCameraScan = () => {
    const { showCameraScanModal, hideCameraScanModal, runCodeScanInfo } =
      this.props

    const onScanSuccess = (barcodeText) => {
      // Log scan for analytics
      this.props.createItem({ key: 'barcodeScan', value: barcodeText })

      // Parse barcode using existing parser
      const codeScanInfo = parseBarcode(barcodeText)

      // Use existing Redux action
      runCodeScanInfo(
        codeScanInfo,
        { ...info(), defaultDurations: this.props.defaultDurations },
        this.createHandleScanError(barcodeText),
      )

      hideCameraScanModal()
    }

    // Show camera modal for camera scanning
    showCameraScanModal(onScanSuccess)
  }

  handleKeyboardScan = () => {
    const { showKeyboardScanModal, hideKeyboardScanModal, runCodeScanInfo } =
      this.props

    const onScanSuccess = (barcodeText) => {
      stopScan()

      // Log scan for analytics
      this.props.createItem({ key: 'barcodeScan', value: barcodeText })

      // Parse barcode using existing parser
      const codeScanInfo = parseBarcode(barcodeText)

      // Use existing Redux action
      runCodeScanInfo(
        codeScanInfo,
        { ...info(), defaultDurations: this.props.defaultDurations },
        this.createHandleScanError(barcodeText),
      )

      hideKeyboardScanModal()
    }

    const onScanCancel = () => {
      stopScan() // Clean up keyboard listener if active
      hideKeyboardScanModal()
    }

    // Show modal and start keyboard listener for barcode scanner
    showKeyboardScanModal(onScanSuccess)
    startScan(onScanSuccess, onScanCancel)
  }

  handleStopScan = () => {
    stopScan()
  }

  createHandleScanError = (code) => (error) => {
    var errorMessage = ''

    switch (error.code) {
      case 'airlineCode.notFound':
        errorMessage = 'Airline code is not found in this information'
        break
      case 'airline.notFound':
        errorMessage = `Airline is not found for given ${error.airlineCode}`
        break
      case 'context.notSupported':
        errorMessage =
          "Code information's context is not supported (Please check your keyboard layout.)"
        break
      case 'confirmation.notFound':
        errorMessage = 'Confirmation Number is not found in information'
        break
      case 'reservation.notFound':
        errorMessage = `Reservation is not found for given ${
          error.confirmationNumber
        }`
        break

      default:
        errorMessage = 'Unknown Error!'
        break
    }

    toastr.error('Error', errorMessage)
  }

  componentWillReceiveProps(nextProps) {
    // Stop scan when keyboard modal is hidden
    if (
      this.props.isKeyboardScanModalShow &&
      !nextProps.isKeyboardScanModalShow
    ) {
      this.handleStopScan()
    }
  }

  componentDidMount() {
    // If editing existing serviceTxn with a product, fetch product details immediately
    const { serviceTxn } = this.props
    if (
      serviceTxn.productId &&
      !this.fetchedProductIds.has(serviceTxn.productId)
    ) {
      this.fetchedProductIds.add(serviceTxn.productId)
      this.props.fetchProduct(serviceTxn.productId)
    }
  }

  componentDidUpdate(prevProps) {
    const { serviceTxn, isCreate } = this.props

    // In update mode, when serviceTxn is loaded and has productId, fetch product price
    if (
      !isCreate &&
      serviceTxn.productId &&
      !prevProps.serviceTxn._id &&
      serviceTxn._id // serviceTxn just loaded
    ) {
      this.props.upateProductPrice()

      // Also fetch product details to display product name (only if not already fetched)
      if (!this.fetchedProductIds.has(serviceTxn.productId)) {
        this.fetchedProductIds.add(serviceTxn.productId)
        this.props.fetchProduct(serviceTxn.productId)
      }
    }

    // Fetch product details when productId changes (e.g., when selecting from modal)
    if (
      serviceTxn.productId &&
      serviceTxn.productId !== prevProps.serviceTxn.productId
    ) {
      if (!this.fetchedProductIds.has(serviceTxn.productId)) {
        this.fetchedProductIds.add(serviceTxn.productId)
        this.props.fetchProduct(serviceTxn.productId)
      }
    }
  }

  isCheckedOut = () => {
    return this.props.serviceTxn.serviceInfo.outTime !== undefined
  }

  isShowProductSelect = () => {
    return isProductRequired(this.props.serviceTxn)
  }

  handleClearForm = () => {
    // Clear the fetched product IDs cache when clearing the form
    this.fetchedProductIds.clear()
    // Call the parent's clear form handler
    if (this.props.onClearForm) {
      this.props.onClearForm()
    }
  }

  serviceTxnDidConfirm = () => {
    this.fetchedProductIds.clear()
    this.props.clearCheckIn()
    toastr.success(I18n.t('cancelServiceTxnModal.toast'))
  }

  render() {
    return (
      <div>
        <ServiceTxnCancelConfirmModal
          serviceTxn={this.props.serviceTxn}
          serviceTxnDidConfirm={this.serviceTxnDidConfirm}
        >
          {(showModal) => (
            <CheckInForm
              serviceTxn={this.props.serviceTxn}
              errors={this.props.errors}
              onFieldChange={this.handleFieldChange}
              onServiceTxnSelect={this.handleServiceTxnSelect}
              loadServiceTxns={this.loadServiceTxns}
              travelClassOptions={this.travelClassOptions}
              onSubmit={this.handleSubmit}
              clearForm={this.handleClearForm}
              submitted={this.props.submitted}
              touch={this.props.touch}
              pristine={this.props.pristine}
              checkoutable={this.props.checkoutable}
              isCheckedOut={this.isCheckedOut()}
              openCheckoutModal={this.openCheckoutModal}
              renderInfo={this.renderInfo}
              onCameraScan={this.handleCameraScan}
              onKeyboardScan={this.handleKeyboardScan}
              kioskServiceInfo={info()}
              isShowProductSelect={this.isShowProductSelect()}
              cancelable={isCancelable(this.props.serviceTxn)}
              openCancelModal={showModal}
              isSubmitting={this.props.isSubmitting}
              openProductModal={this.openProductModal}
              selectedProduct={this.props.selectedProduct}
            />
          )}
        </ServiceTxnCancelConfirmModal>

        {this.props.checkoutable && (
          <CheckoutModal
            isShow={this.state.isCheckoutModalOpen}
            onHide={this.closeCheckoutModal}
            serviceTxn={this.props.serviceTxn}
            onCheckOut={this.handleCheckOut}
          />
        )}

        <ProductSelectModal
          isShow={this.state.isProductModalOpen}
          onClose={this.closeProductModal}
          onProductSelect={this.handleProductSelect}
          serviceId={info().serviceId}
          searchText={this.state.productSearchText}
          onSearchChange={this.handleProductSearchChange}
        />
      </div>
    )
  }
}

const reduxConnect = connect(
  ({ checkin, fetch }) => {
    const productList =
      fetch.product && fetch.product.list ? fetch.product.list : []
    const singleProduct =
      fetch.product && fetch.product.single ? fetch.product.single : null
    const productId = checkin.serviceTxn.productId

    // Find selected product:
    // 1. First try from list (when selecting from modal)
    // 2. Then try single fetched item (when editing or after fetch)
    let selectedProduct = null
    if (productId) {
      selectedProduct = productList.find((p) => p._id === productId)
      if (
        !selectedProduct &&
        singleProduct &&
        singleProduct._id === productId
      ) {
        selectedProduct = singleProduct
      }
    }

    return {
      serviceTxn: checkin.serviceTxn,
      errors: checkin.errors,
      submitted: checkin.submitted,
      touch: checkin.touch,
      pristine: checkin.pristine,
      isCreate: checkin.isCreate,
      checkoutable: checkin.checkoutable,
      defaultDurations:
        fetch.defaults && fetch.defaults.list
          ? fetch.defaults.list.durations
          : undefined,
      selectedProduct,
    }
  },
  {
    updateCheckInServiceTxnField,
    updateServiceInfo,
    runCodeScanInfo,
    createItem,
    clearCheckIn,
    upateProductPrice,
    fetchProduct,
  },
)

const enhancer = compose(
  reduxConnect,
  withCheckInType,
  withCameraScanModal,
  withKeyboardScanModal,
)

export default enhancer(CheckInFormContainer)
